import { useMemo, useState } from 'react';
import type { LastEntry } from '../api';
import { CATEGORIES, type ExerciseDef } from '../exercises';
import type { EntryKind } from '../types';
import { Icon } from './Icon';
import { Sheet } from './Sheet';

type Props = {
  /** 過去に手入力した種目名 (カタログに無いもの) */
  customNames: string[];
  lastByExercise: Record<string, LastEntry>;
  onPick: (def: ExerciseDef) => void;
  onClose: () => void;
};

const KIND_BADGE: Record<EntryKind, string> = {
  weight: '重量',
  reps: '回数',
  hold: '秒数',
  cardio: '有酸素',
};

export function ExercisePicker({ customNames, lastByExercise, onPick, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [openCategory, setOpenCategory] = useState<string | null>(CATEGORIES[0]?.id ?? null);

  // 手入力の種目は「その他」としてまとめて出す
  const groups = useMemo(() => {
    const custom = customNames
      .filter((name) => !CATEGORIES.some((c) => c.items.some((i) => i.name === name)))
      .map((name): ExerciseDef => ({ name, kind: lastByExercise[name]?.kind ?? 'weight' }));

    return custom.length > 0
      ? [...CATEGORIES, { id: 'custom', label: 'その他 (手入力)', short: '他', items: custom }]
      : CATEGORIES;
  }, [customNames, lastByExercise]);

  const q = query.trim();
  const matches = useMemo(() => {
    if (!q) return null;
    return groups.flatMap((g) =>
      g.items.filter((i) => i.name.includes(q)).map((i) => ({ ...i, category: g.label })),
    );
  }, [groups, q]);

  const renderItem = (item: ExerciseDef, category?: string) => {
    const last = lastByExercise[item.name];
    return (
      <button key={item.name} className="pick__item" onClick={() => onPick(item)}>
        <span className="pick__name">
          {item.name}
          {category && <span className="pick__cat">{category}</span>}
        </span>
        <span className="pick__meta">
          {last ? <span className="pick__last">前回 {last.date.slice(5)}</span> : null}
          <span className={`pick__kind pick__kind--${item.kind}`}>{KIND_BADGE[item.kind]}</span>
        </span>
      </button>
    );
  };

  return (
    <Sheet
      title={
        <div className="sheet__title">
          <span className="sheet__date">器具を選ぶ</span>
        </div>
      }
      onClose={onClose}
    >
      <input
        className="field"
        value={query}
        placeholder="種目名で検索 / 無い種目はそのまま入力して追加"
        autoComplete="off"
        onChange={(e) => setQuery(e.target.value)}
      />

      {matches ? (
        <div className="pick__list" style={{ marginTop: 14 }}>
          {matches.length > 0 ? (
            matches.map((m) => renderItem(m, m.category))
          ) : (
            <>
              <div className="empty">「{q}」はカタログにありません</div>
              <button className="btn btn--primary btn--wide" onClick={() => onPick({ name: q, kind: 'weight' })}>
                「{q}」を重量種目として追加
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="pick__cats">
          {groups.map((cat) => {
            const open = openCategory === cat.id;
            return (
              <section key={cat.id} className={`pick__cat-block${open ? ' is-open' : ''}`}>
                <button
                  className="pick__cat-head"
                  onClick={() => setOpenCategory(open ? null : cat.id)}
                  aria-expanded={open}
                >
                  <span className="pick__cat-mark" aria-hidden>
                    {cat.short}
                  </span>
                  <span className="pick__cat-label">{cat.label}</span>
                  <span className="pick__cat-count mono">{cat.items.length}</span>
                  <Icon name={open ? 'chevronUp' : 'chevronDown'} size={15} />
                </button>
                {open && <div className="pick__list">{cat.items.map((i) => renderItem(i))}</div>}
              </section>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}
